import * as React from "react";
import styled from "styled-components";
import { trpc } from "../trpc";
import { Tree } from "src/utils/Tree";

interface Props {
  value: string | null;
  onChange(categoryId: string | null): void;
}

export function CategorySelect({ value, onChange }: Props) {
  const root = React.useRef<HTMLDivElement>(null);
  const [search, setSearch] = React.useState("");
  const { data: categories, isLoading } = trpc.getCategories.useQuery();
  const [isOpen, setOpen] = React.useState(false);
  const selected = categories && !!value && Tree.find(categories, (c) => c.id === value);

  const setValue = (value: string | null) => {
    setOpen(false);
    onChange(value);
  };

  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as HTMLElement)) {
        setOpen(false);
      }
    };

    document.body.addEventListener("click", handler);
    return () => document.body.removeEventListener("click", handler);
  });

  return (
    <Container ref={root}>
      {isLoading && <Placeholder>Loading...</Placeholder>}
      {!isLoading && (
        <Input
          type="text"
          value={isOpen ? search : ""}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={(selected && selected.name) || ""}
        />
      )}
      {isOpen && (
        <Children>
          {!search && (
            <Option onClick={() => setValue(null)}>
              <Placeholder>None</Placeholder>
            </Option>
          )}
          {Tree.walkWithAncestors(categories ?? [])
            .toArray()
            .filter((cat) => cat.name?.toLowerCase().includes(search))
            .map((cat) => (
              <Option
                key={cat.id}
                onClick={() => setValue(cat.id)}
                style={{ textIndent: search ? 0 : 20 * cat.ancestry.length }}
              >
                <OptionName>{cat.name}</OptionName>
                {search && (
                  <OptionCrumbs>{cat.ancestry.map((a) => a.name).join(" - ")}</OptionCrumbs>
                )}
              </Option>
            ))}
        </Children>
      )}
    </Container>
  );
}

const Input = styled.input`
  padding: 5px 8px;
  border: 1px solid #999;
  background: var(--box-color);
  border-radius: 3px;
  width: 100%;
  font-size: 14px;
`;

const Container = styled.div`
  display: block;
  position: relative;
`;

const Placeholder = styled.div`
  opacity: 0.5;
`;

const Children = styled.div`
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  min-width: 100%;
  margin-top: -1px;
  background: var(--box-color);
  white-space: nowrap;
  border: 1px solid #999;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.25);
`;

const Option = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 3px 8px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: rgba(0, 0, 0, 0.125);
  }
`;

const OptionName = styled.div``;

const OptionCrumbs = styled.div`
  opacity: 0.5;
`;
